import { type FC, useCallback, useEffect, useState } from 'react';
import BaseLayout from '@/base/Layout';
import BaseLayoutBody from '@/base/Layout/Body';
import { UiHStack, UiSimpleGrid, UiStack, UiText } from '@/lib/ui';
import AdminSideNav from '@/registration/component/Layout/AdminSideNav';
import BaseLayoutFullScreenToggle from '@/base/Layout/FullScreenToggle';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import BaseSimpleSelectElement, { type Option as SelectElementOption } from '@/base/Form/SimpleSelectElement';
import type { ActionMeta, OnChangeValue, OptionBase } from 'chakra-react-select';
import { useAttendeeCategoryQuery } from '@/registration/hook/useAttendeeCategoryQuery';
import InsightTable from './InsightTable';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import SimpleMultiSelectElement, { type Option } from '@/base/Form/SimpleMultiSelectElement';
import SmartWindow from '@/base/Layout/SmartWindow';
import BaseAppListDrawerButton from '@/base/App/ListDrawerButton';
import BaseAppRegistrationCard from '@/base/App/RegistrationCard';
import { useEventOptionsQuery } from '@/registration/hook/useEventOptions';

interface CustomOption extends OptionBase {
  label: string
  value: string
}

const InsightPage: FC = () => {
  const { tenantCode } = useRegisterRoute();
  const [eventId, setEventId] = useState<string>('');
  const [attendeeCategoryIds, setAttendeeCategoryIds] = useState<CustomOption[]>([]);

  const { data: attendeeCategories } = useAttendeeCategoryQuery(eventId);
  const { eventOptions, isLoading: isLoadingEvent } = useEventOptionsQuery();

  const attendeeCategoryOptions = (attendeeCategories?.length ? attendeeCategories : []).map(({ id: value, name: label }) => {
    return {
      value: value.toString(),
      label
    };
  });

  const handleChangeEvent = useCallback(
    (option: OnChangeValue<SelectElementOption, false>) => {
      setEventId(option?.value?.toString() ?? '');
    },
    []
  );

  const handleChange = (newValue: OnChangeValue<Option, boolean>, actionMeta: ActionMeta<Option>) => {
    setAttendeeCategoryIds(newValue as CustomOption[]);
  };

  useEffect(() => {
    if (attendeeCategoryOptions.length === 0 && attendeeCategoryIds.length > 0) {
      setAttendeeCategoryIds([]);
    }
  }, [attendeeCategoryOptions, attendeeCategoryIds]);

  const categoryIds = attendeeCategoryIds.map(({ value }) => { return Number(value); });

  return (
    <BaseLayout
      smartWindow={(<SmartWindow />)}
      sideMenu={(<AdminSideNav activeNodes={['dashboard', 'insight']} tenantCode={tenantCode} />)}
      appSwitch={(<BaseAppListDrawerButton currentApp={<BaseAppRegistrationCard showDescription={false} />} currentAppName={'registration'} />)}
    >
      <BaseLayoutBody isStandalone={false} >
        <UiHStack justifyContent='flex-start' spacing={4}>
          <BaseLayoutFullScreenToggle />
          <UiText variant={'title'}>Insights</UiText>
        </UiHStack>
        <UiStack mt={4}>
          <UiSimpleGrid columns={4} spacing={8}>
            <UiStack flexGrow={1} >
              <BaseSimpleSelectElement
                optionValue={eventId}
                onChange={handleChangeEvent}
                isLoading={isLoadingEvent}
                options={eventOptions}
                placeholder={'Select event'}
              />
            </UiStack>
            <UiStack flexGrow={1}>
              <SimpleMultiSelectElement
                optionValue={eventId}
                onChange={handleChange}
                options={attendeeCategoryOptions}
                value={attendeeCategoryIds}
                placeholder={'Select attendee category'}
              />
            </UiStack>
          </UiSimpleGrid>
        </UiStack>
        <BaseDividerHorizontal height={8} />
        <UiStack height={4} />
        {!!eventId && Boolean(categoryIds.length) && (
          <UiStack mt={5}>
            <InsightTable eventId={Number(eventId)} categoryIds={categoryIds} />
          </UiStack>
        )}
      </BaseLayoutBody>
    </BaseLayout>
  );
};

export default InsightPage;
